import { Box, Chart, Stat, StatLabel, StatNumber, dayLabel } from '~/shared/ui'
import { useStatisticEarningFee, useStatisticEarningFeeHistory } from '../api'
import { CALENDAR, PeriodFigures } from '~/team_tools/dashboard/statistic/figures'
import { usePeriod, useWindowParams } from '~/team_tools/dashboard/statistic/period'

const CHART_DAYS = 30

// Green for money in, matching the USD line on the store sales chart. The two
// sections sit a few rows apart and both plot dollars; a different colour for
// the same unit would imply a distinction that is not there.
const FEE_COLOUR = 'rgb(34, 197, 94)'

const FeeHistoryChart = () => {
    const { data } = useStatisticEarningFeeHistory({ days: CHART_DAYS })
    const history = data?.history ?? {}
    const labels = Object.keys(history).map(dayLabel)
    // The RPC encoder renders Decimal as a float, so these are already numbers;
    // Number() is a guard against that encoder ever changing, since a numeric
    // string plots as a flat line at zero rather than failing visibly.
    const values = Object.values(history).map(Number)

    return (
        <Chart.TimeSeries
            className='h-48 sm:h-56'
            data={{
                labels,
                datasets: [
                    {
                        label: 'fees collected',
                        data: values,
                        borderColor: FEE_COLOUR,
                        backgroundColor: FEE_COLOUR,
                    },
                ],
            }}
            options={{
                scales: {
                    y: {
                        min: 0,
                        // precision: 0 is wrong for money -- a day that took $4.50
                        // would tick as 4 and 5 and read as a rounding bug.
                        ticks: {
                            maxTicksLimit: 5,
                            font: { size: 10 },
                            callback: (value: number) => `$${value}`,
                        },
                        grid: { color: 'rgba(255,255,255,0.06)' },
                    },
                },
            }}
        />
    )
}

/** Dollars, to the cent. The tiles used to print the raw 4-decimal figure with
 *  no unit ("2.4667"), and nothing at all for an empty table. */
const formatFee = (value: unknown) =>
    `$${Number(value ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const FEE_AXIS = {
    scales: {
        y: {
            min: 0,
            ticks: { maxTicksLimit: 5, font: { size: 10 }, callback: (value: number) => `$${value}` },
            grid: { color: 'rgba(255,255,255,0.06)' },
        },
    },
}

/** On the dashboard: the chosen window only, its number over its chart, the
 *  same shape as every other tab. */
const WindowedFees = () => {
    const window = useWindowParams()
    const { data, isLoading, error } = useStatisticEarningFee(window.figure)
    const { data: history } = useStatisticEarningFeeHistory(window.history)

    return (
        <div className='flex w-full flex-col gap-1 text-center'>
            <header className='text-xl font-semibold'>USDC fees kept</header>
            <small className='text-white/50'>
                The cut we keep from a player's USDC payout when they die. Spawn fees are not included, and payouts
                later revoked are left out.
            </small>
            {error && (
                <small className='text-red-300'>
                    {error?.error?.code === 403
                        ? 'Your role cannot see collected fees.'
                        : 'Collected fees could not be loaded.'}
                </small>
            )}
            <PeriodFigures
                periods={CALENDAR}
                loading={isLoading}
                value={(key) => (data ? formatFee(data[key]) : error ? 'n/a' : undefined)}
                chart={{
                    lines: [{ label: 'USDC fees kept', colour: FEE_COLOUR, history: history?.history }],
                    options: FEE_AXIS,
                }}
            />
        </div>
    )
}

export const StatisticEarningFee = () => (usePeriod() ? <WindowedFees /> : <AllFees />)

const AllFees = () => {
    const { data, isLoading, error } = useStatisticEarningFee()
    const keys = {
        total: 'Total',
        today: 'Today',
        week: 'Week',
        month: 'Month',
        hours_24: '24 hours',
        days_7: '7 days',
        days_30: '30 days',
    }
    return (
        <div className='flex flex-col gap-1'>
            <header className='text-xl font-semibold'>Collected Fees</header>
            <small className='text-white/50'>
                The service fee kept back from match earnings paid to players. Payouts later revoked are left out. Spawn
                fees are not included. Fraud-tagged accounts are included: this is money that moved.
            </small>
            {/* The endpoint is Marketing-only and the Dashboard is open to all
                staff. A refusal used to render as seven blank tiles. */}
            {error && (
                <small className='text-red-300'>
                    {error?.error?.code === 403
                        ? 'Your role cannot see collected fees.'
                        : 'Collected fees could not be loaded.'}
                </small>
            )}
            <div className='grid grid-cols-1 md:grid-cols-3 gap-2 text-center mt-2 w-full'>
                {Object.entries(keys).map(([k, v], i) => (
                    <Stat.Gray key={i} className={i === 0 && 'col-start-1 col-end-[-1]'} loading={isLoading}>
                        <StatLabel className='text-amber-500'>{v}</StatLabel>
                        <StatNumber>{data ? formatFee(data[k]) : error ? 'n/a' : undefined}</StatNumber>
                    </Stat.Gray>
                ))}
            </div>

            {/* Daily shape under the running totals. The tiles answer "how much",
                which a single number cannot distinguish from "how much, and is it
                falling" -- a month that is down 40% week on week reads as a healthy
                total right up until it does not. */}
            <Box className='w-full gap-2 pt-4'>
                <small className='text-white/50'>
                    Fees collected per day, last {CHART_DAYS} days. A session counts on the day it ended.
                </small>
                <Box.Gray className='w-full'>
                    <FeeHistoryChart />
                </Box.Gray>
            </Box>
        </div>
    )
}
